// src/routes/ModuleRoute.tsx
//
// The single react-router route element for `/m/:moduleKey/:tab`.
// Reads params, consults the registry + page dispatcher, renders:
// - the page component when registered
// - a "Coming soon" card when the registry marks the tab or module as
//   `comingSoon` and no concrete page exists
// - a NotFound card for truly unknown routes (bad URL, typo, deleted route)
//
// The actual auth gate lives in App.tsx's root-level redirect — by the time
// ModuleRoute renders we already know the user is authenticated.

import { ArrowLeft, Sparkles } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { findItem, findModule } from '@/modules';
import { useModules } from '@/context/ModulesContext';
import { hasConcretePage, resolvePage, type PageProps } from './pages';

/** Legacy URL redirects — when we move or rename a tab we keep bookmarks
 *  working by mapping the old path to the new one. */
/** Pantallas que se abren desde Configuración (/m/ajustes, «Más ajustes»): llevan
 *  «← Configuración» arriba para volver. Todas sus pestañas, entres por donde entres. */
const DESDE_AJUSTES = new Set(['empresa', 'config']);

const LEGACY_REDIRECTS: Record<string, string> = {
  'travel-sales/company': '/m/empresa/company',
  // 2026-10-22 · «Difusión WhatsApp» (/m/whatsapp/*) se integró a la app
  // WhatsApp. Su Inbox duplicaba Chats → va a Chats. Si la org no tiene la app
  // (sin número), ModuleRoute la manda a su inicio, como cualquier módulo ajeno.
  'whatsapp': '/m/whatsapp-app/chats',
  'whatsapp/inbox': '/m/whatsapp-app/chats',
  'whatsapp/contacts': '/m/whatsapp-app/difusion?s=contactos',
  'whatsapp/templates': '/m/whatsapp-app/difusion?s=plantillas',
  'whatsapp/broadcasts': '/m/whatsapp-app/difusion?s=envios',
  // Add more as needed
};

interface ModuleRouteProps {
  pageProps: PageProps;
}

export function ModuleRoute({ pageProps }: ModuleRouteProps) {
  const { moduleKey, tab } = useParams<{ moduleKey: string; tab?: string }>();
  const { availableModules, defaultModuleKey, loading, isAdmin, userRoles, sovereign } = useModules();

  if (!moduleKey) {
    return <Navigate to="/" replace />;
  }

  // Legacy redirect (old URL → new URL). Kept outside the module/item
  // validation so bookmarks don't 404 after a reorg.
  const legacyKey = tab ? `${moduleKey}/${tab}` : moduleKey;
  if (LEGACY_REDIRECTS[legacyKey]) {
    return <Navigate to={LEGACY_REDIRECTS[legacyKey]} replace />;
  }

  // Unknown module → 404 card (stay in Layout so user has nav to recover).
  const module = findModule(moduleKey);
  if (!module) {
    return <NotFound message={`Módulo "${moduleKey}" no existe.`} />;
  }

  // Módulo EXISTE en el registry pero NO está habilitado para esta org/arquetipo
  // (ej. entrar por URL directa a /m/travel-sales en una clínica) → al home. Así
  // el dashboard NUNCA muestra un módulo ajeno al arquetipo, ni por URL/caché.
  if (!loading && availableModules.length > 0 &&
      !availableModules.some((m) => m.key === moduleKey)) {
    const home = defaultModuleKey();
    if (home && home !== moduleKey) {
      return <Navigate to={`/m/${home}`} replace />;
    }
  }

  // Bare /m/:moduleKey → redirect to the module's default tab.
  if (!tab) {
    const first = module.sidebarItems[0]?.tab;
    return first ? (
      <Navigate to={`/m/${moduleKey}/${first}`} replace />
    ) : (
      <NotFound message={`"${module.name}" no tiene pestañas definidas.`} />
    );
  }

  const match = findItem(moduleKey, tab);
  if (!match) {
    return <NotFound message={`"${moduleKey}/${tab}" no está registrado.`} />;
  }

  // Pestaña solo-admin abierta por URL directa (o caché) por quien no es admin
  // → a la primera pestaña que sí puede ver. El servidor igual le niega los datos.
  const esDev = !!userRoles?.some((r) => r === 'cto' || r === 'developer');
  if (match.item.requireAdmin && !isAdmin && !esDev) {
    const permitida = module.sidebarItems.find((s) => !s.requireAdmin)?.tab;
    if (permitida) return <Navigate to={`/m/${moduleKey}/${permitida}`} replace />;
  }

  // Pestaña bloqueada en orgs soberanas (p. ej. /m/orion/calendario = Google) → al home.
  if (match.item.hideForSovereign && sovereign) {
    return <Navigate to={`/m/${defaultModuleKey()}`} replace />;
  }

  const page = resolvePage(moduleKey, tab, pageProps);
  if (page) {
    const volver = DESDE_AJUSTES.has(moduleKey) && availableModules.some((m) => m.key === 'ajustes');
    return volver ? (
      <>
        <BackToAjustes />
        {page}
      </>
    ) : (
      <>{page}</>
    );
  }

  // No concrete page yet but registry says it's expected (comingSoon)
  if (match.item.comingSoon || match.module.comingSoon) {
    return <ComingSoon module={match.module.name} item={match.item.label} />;
  }

  // Registered in the sidebar but missing from the dispatch map — treat as
  // a broken build rather than a 404. Fail loudly so the dev notices.
  return (
    <NotFound
      message={`Falta registrar "${moduleKey}/${tab}" en routes/pages.tsx.`}
    />
  );
}


function BackToAjustes() {
  return (
    <nav aria-label="Regresar" className="mx-auto w-full max-w-6xl px-3 pt-3 sm:px-6 lg:px-8">
      <Link
        to="/m/ajustes/inicio?s=mas"
        className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <ArrowLeft size={16} aria-hidden /> Configuración
      </Link>
    </nav>
  );
}


function NotFound({ message }: { message: string }) {
  return (
    <div className="o-card p-8 text-center">
      <svg className="mx-auto mb-3 w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      <p className="text-white/70 font-semibold">Ruta no encontrada</p>
      <p className="text-sm text-white/50 mt-1">{message}</p>
    </div>
  );
}


function ComingSoon({ module, item }: { module: string; item: string }) {
  return (
    <div className="o-card p-10 text-center">
      <Sparkles size={32} className="mx-auto mb-4 text-[var(--acc1,#00e5ff)]" />
      <h2 className="text-xl font-semibold text-white/90">
        {item} — próximamente
      </h2>
      <p className="mt-2 text-sm text-white/60 max-w-md mx-auto">
        Esta sección del módulo <strong>{module}</strong> está en camino.
        Mientras tanto, el backend ya expone los endpoints; el frontend aterriza
        en los siguientes PRs del Track E.
      </p>
    </div>
  );
}

export default ModuleRoute;
